import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import { computeTurkeyForumIndex } from '../utils/turkeyForumIndex';
import type { ForumBase } from '../types/forum';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface Props {
  darkMode: boolean;
}

const TurkeyForumIndexPage: React.FC<Props> = ({ darkMode }) => {
  const [forums, setForums] = useState<ForumBase[]>([]);

  usePageSeo({
    title: 'Türkiye Forum Endeksi (TFE)',
    description: 'Türkçe forum ekosisteminin aylık büyüme endeksi ve özet istatistikleri.',
    keywords: 'forum endeksi, TFE, türkiye',
    canonical: '/index',
  });

  useEffect(() => {
    supabase
      .from('forums')
      .select('*')
      .eq('is_active', true)
      .then(({ data }) => setForums(data || []));
  }, []);

  const tfe = computeTurkeyForumIndex(forums);
  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Türkiye Forum Endeksi
        </h1>
        <p className={`mb-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {tfe.monthLabel} — aktif forumların üye büyümesine dayalı bileşik gösterge (baz: 100).
        </p>
        <div className={`rounded-2xl border p-8 text-center ${card}`}>
          <p className={`text-sm uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>TFE</p>
          <p className="text-6xl font-bold text-indigo-600 dark:text-indigo-400 my-2">{tfe.index}</p>
          <div className="flex justify-center items-center gap-2">
            {tfe.avgMemberGrowth === null ? (
              <Minus className="text-gray-400" />
            ) : tfe.avgMemberGrowth >= 0 ? (
              <TrendingUp className="text-green-500" />
            ) : (
              <TrendingDown className="text-red-500" />
            )}
            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
              Ortalama üye büyümesi:{' '}
              {tfe.avgMemberGrowth !== null ? `%${tfe.avgMemberGrowth}` : '—'}
            </span>
          </div>
        </div>
        <dl className={`mt-6 grid grid-cols-2 gap-4 ${card} rounded-xl border p-6`}>
          <div>
            <dt className="text-sm text-gray-500">Aktif forum</dt>
            <dd className="text-2xl font-semibold">{tfe.activeCount}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Toplam üye</dt>
            <dd className="text-2xl font-semibold">{tfe.totalMembers.toLocaleString('tr-TR')}</dd>
          </div>
        </dl>
        <p className={`mt-6 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          <Link to="/rising" className="text-indigo-500 hover:underline">
            Yükselen forumlar
          </Link>{' '}
          ve{' '}
          <Link to="/leaderboard" className="text-indigo-500 hover:underline">
            lig tablosu
          </Link>{' '}
          ile detaylı inceleyin.
        </p>
      </div>
    </div>
  );
};

export default TurkeyForumIndexPage;
